import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Pulse news cover mock at coverAspectRatio (default 5:2).
 * Cover is a clean photo crop — title is NOT burned onto the image.
 * Pulse shows `cover_title` + `summary` as separate text under the banner.
 */
function DiscoverCardPreview({
  coverTitle,
  summary,
  coverImageUrl,
  slideCount,
  coverAspectRatio = '5:2',
  focusX,
  focusY,
  showTitleOverlay = false,
  showSlideCountBadge = false,
  imageKey,
}) {
  const aspectCss = coverAspectRatio.replace(':', ' / ');
  const xPct =
    typeof focusX === 'number' && !Number.isNaN(focusX)
      ? Math.min(1, Math.max(0, focusX)) * 100
      : 50;
  const yPct =
    typeof focusY === 'number' && !Number.isNaN(focusY)
      ? Math.min(1, Math.max(0, focusY)) * 100
      : 50;
  const objectPosition = ` ${xPct}% ${yPct}%`;

  return (
    <Box sx={{ maxWidth: 400, width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
          position: 'relative',
          aspectRatio: aspectCss,
          bgcolor: '#1a1a2e',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}
      >
        {coverImageUrl ? (
          <Box
            component="img"
            key={imageKey || coverImageUrl}
            src={coverImageUrl}
            alt={coverTitle || 'Cover'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition,
              display: 'block',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#2d2d44',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No cover (5:2 banner)
            </Typography>
          </Box>
        )}
        {showTitleOverlay && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.85) 100%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              p: 1.5,
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                fontWeight: 700,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {coverTitle || 'Untitled'}
            </Typography>
          </Box>
        )}
        {showSlideCountBadge && slideCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: 10,
            }}
          >
            {slideCount} slides
          </Box>
        )}
      </Box>
      {coverTitle ? (
        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700, lineHeight: 1.3 }}>
          {coverTitle}
        </Typography>
      ) : null}
      {summary ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {summary}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Pulse cover · {coverAspectRatio} (clean crop — title + summary are separate text)
      </Typography>
    </Box>
  );
}

export default DiscoverCardPreview;
